<template>
  <ul
    v-if="hasOptions && !!locationId"
    :style="style"
    class="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
  >
    <graphql-requester
      v-for="(option, key) in sortedOptions"
      :key="key"
      :disabled="!!option.providers"
      :gql="$options.GET_PROVIDERS"
      :variables="{ input: { ...input, service_id: option.id } }"
      v-slot="{ data, loading }"
    >
      <li class="flex flex-col" :data-service="option.id">
        <a
          class="h-full flex flex-col border rounded p-3 cursor-pointer select-none bg:hover"
          :data-testid="`service-${option.name}`"
          href="javascript:;"
          :class="{
            'bg:selected': match(option, value),
            'opacity-50 pointer-events-none':
              loading || (!(option.providers || data) && !loading),
          }"
          @click="onClick(option, data)"
        >
          <span class="flex flex-col space-y-2 text-center my-auto">
            <span v-if="loading" class="flex flex-col py-2">
              <span class="mx-auto w-10 h-10">
                <svg-loading-one />
              </span>
            </span>
            <span
              v-else-if="(option.providers || data) && !loading && displayIcons"
              class="flex flex-col py-2"
            >
              <span class="mx-auto w-10 h-10">
                <img
                  class="max-w-full"
                  width="40"
                  :src="option.icon_url"
                  :alt="option.name"
                />
              </span>
            </span>
            <alert-warning v-else-if="displayIcons" class="h-10"
              >Something went wrong</alert-warning
            >
            <span :class="['flex flex-col', { 'py-2': !displayIcons }]">
              <span class="text-sm font-bold break-words relative">
                <truncated-text
                  :text="option.name"
                  custom-class="w-full inline-block"
                />
              </span>
            </span>
            <span v-if="option.description" class="flex flex-col">
              <span class="text-sm leading-tight text-gray-500">
                {{ option.description }}
              </span>
            </span>
          </span>
        </a>
      </li>
    </graphql-requester>
  </ul>

  <!-- placeholder for if the user has not selected a patient type-->
  <ul v-else class="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
    <li class="flex flex-col">
      <span
        class="h-full flex flex-col border rounded p-3 select-none placeholder-color"
      >
        <span class="flex flex-col space-y-2 text-center my-auto">
          <span class="flex flex-col py-2">
            <span
              class="text-sm font-bold break-words relative"
              style="min-height: 24px"
            >
            </span>
          </span>
        </span>
      </span>
    </li>
    <li class="flex flex-col lg:hide-on-mobile">
      <span
        class="h-full flex flex-col border rounded p-3 select-none placeholder-color"
      >
        <span class="flex flex-col space-y-2 text-center my-auto">
          <span class="flex flex-col py-2">
            <span
              class="text-sm font-bold break-words relative"
              style="min-height: 24px"
            >
            </span>
          </span>
        </span>
      </span>
    </li>
  </ul>
</template>

<script>
import orderBy from "lodash.orderby";
import { GET_PROVIDERS } from "@/graphql";
import GraphqlRequester from "@/components/GraphqlRequester";
import AlertWarning from "@/components/Alert/AlertWarning";
import SvgLoadingOne from "@/components/Svg/SvgLoadingOne";
import TruncatedText from "@/components/Info/TruncatedText.vue";

export default {
  GET_PROVIDERS,
  components: {
    TruncatedText,
    GraphqlRequester,
    AlertWarning,
    SvgLoadingOne,
  },
  props: {
    options: {
      type: Array,
      default: () => [],
    },
    value: Object,
    controls: Object,
  },
  computed: {
    sortedOptions() {
      return orderBy(
        this.options || [],
        [(el) => el.name.toLowerCase()],
        ["asc"]
      );
    },
    hasOptions() {
      return this.options && this.options.length > 0;
    },
    style() {
      const colors = this.$store.state.config?.colors;
      return {
        "--bg-color": colors?.secondaryOptionColor || "#D4F3BB",
        "--border-color": colors?.primaryOptionColor || "#51B44F",
        "--color": colors?.primaryOptionColor || "#51B44F",
      };
    },
    locationId() {
      return (
        this.$store.getters?.locationId || this.controls?.locations?.location_id
      );
    },
    serviceId() {
      return this.controls?.services?.id;
    },
    displayIcons() {
      return !this.$store.getters.hideServiceIcons;
    },
    input() {
      if (!this.locationId && !this.serviceId) return;

      return {
        location_id: this.locationId,
        service_id: this.serviceId,
      };
    },
  },
  methods: {
    match(option, value) {
      const x = { ...option };
      const y = { ...value };
      if (x?.providers) delete x.providers;
      if (y?.providers) delete y.providers;
      return JSON.stringify(x) === JSON.stringify(y);
    },
    onClick(option, data) {
      if (!this.hasOptions) {
        return;
      }
      const providers = option.providers || data?.getProviders || [];
      this.$emit("input", {
        ...option,
        providers: providers,
      });
      this.$store.commit("fieldUpdate", {
        value: providers,
        key: "providers",
      });
    },
  },
};
</script>

<style scoped>
.placeholder-color {
  background-color: #fefefe;
  border-color: #e5e7eb;
}

.lg\:hide-on-mobile {
  display: flex;
  @media (max-width: 1023px) {
    display: none;
  }
}

.bg\:selected {
  background-color: var(--bg-color);
  border-color: var(--border-color);
  box-shadow: 0 0 0 1px var(--border-color);
}

.bg\:hover:hover {
  border-color: var(--border-color);
  box-shadow: 0 0 0 1px var(--border-color);
}

@media (min-width: 768px) {
  .selected {
    color: var(--color);
    background-color: transparent;
  }

  .selected > span:first-child {
    background-color: var(--bg-color);
    border-color: var(--border-color);
  }

  .hovered:hover {
    color: var(--color);
    background-color: transparent;
  }

  .hovered:hover > span:first-child {
    background-color: var(--bg-color);
    border-color: var(--border-color);
  }
}
</style>
